import React from 'react';
import { CargaDocumento } from './CargaDocumento';
import { RegistroCapacitacion } from './RegistroCapacitacion';

// Subvistas del expediente Mobile (M.2B). Cambios es de LECTURA SOLAMENTE.
// Escrituras, siempre con endpoints existentes y confirmación explícita:
// Documentos -> anexar documento (M.2B.2, CargaDocumento.jsx);
// Capacitaciones -> subir registro del empleado (M.2B.3, RegistroCapacitacion.jsx).
// No hay eliminar en Mobile.

const esHttps = url => /^https:\/\//.test(String(url || ''));

function Cargando({ etiqueta }) {
  return (
    <div className="m-sec-cargando" role="status" aria-label={etiqueta}>
      {[0, 1, 2].map(i => <div key={i} className="m-sec-esqueleto"><span></span><span></span></div>)}
    </div>
  );
}

function Vacio({ icono, texto }) {
  return (
    <div className="m-emp-vacio" role="status">
      <i className={`ti ${icono}`} aria-hidden="true"></i>
      <p className="m-emp-vacio-titulo">{texto}</p>
    </div>
  );
}

// ─── Cambios ────────────────────────────────────────────────────────────────
// Fuente: `cambios` que App.jsx ya carga (api/cambios-intellisis), filtrados
// por empleado. El contrato no incluye fecha (la hoja la guarda, pero
// listarCambiosIntellisis no la devuelve); `id` es la fila de la hoja, así
// que el orden descendente muestra primero lo más reciente.
// Tipos de cambio con datos sensibles (salario, SDI, nómina, banco): se
// muestran tipo y estatus, nunca los valores anterior/nuevo.
const TIPO_CAMBIO_SENSIBLE = /salari|sdi|sueldo|nomina|nómina|clabe|banc/i;
const ESTATUS_CAMBIO = {
  Pendiente_Intellisis: { texto: 'Pendiente', tono: 'pendiente' },
  Aplicado_Intellisis: { texto: 'Confirmado', tono: 'confirmado' }
};

export function CambiosMobile({ cambios }) {
  if (cambios.length === 0) return <Vacio icono="ti-arrows-exchange" texto="Sin cambios registrados." />;
  const ordenados = [...cambios].sort((a, b) => (Number(b.id) || 0) - (Number(a.id) || 0));
  return (
    <ul className="m-sec-lista">
      {ordenados.map(c => {
        const estatus = ESTATUS_CAMBIO[c.estatus] || { texto: String(c.estatus || '').replace(/_/g, ' '), tono: 'neutro' };
        const sensible = TIPO_CAMBIO_SENSIBLE.test(String(c.tipo || ''));
        const anterior = sensible ? '' : String(c.anterior == null ? '' : c.anterior).trim();
        const nuevo = sensible ? '' : String(c.nuevo == null ? '' : c.nuevo).trim();
        return (
          <li key={c.id} className="m-sec-item">
            <div className="m-sec-item-cabecera">
              <span className="m-sec-item-titulo">{String(c.tipo || 'Cambio').replace(/_/g, ' ')}</span>
              {estatus.texto && <span className={`m-sec-chip m-sec-chip-${estatus.tono}`}>{estatus.texto}</span>}
            </div>
            {(anterior || nuevo) && (
              <dl className="m-cambio-valores">
                {anterior && <div className="m-cambio-anterior"><dt>Anterior</dt><dd>{anterior}</dd></div>}
                {anterior && nuevo && <i className="ti ti-arrow-right m-cambio-flecha" aria-hidden="true"></i>}
                {nuevo && <div className="m-cambio-nuevo"><dt>Nuevo</dt><dd>{nuevo}</dd></div>}
              </dl>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ─── Capacitaciones ─────────────────────────────────────────────────────────
// Fuente: api/empleado/capacitaciones (mismo endpoint que TabCapacitaciones
// de Desktop). El sistema conserva la relación empleado ↔ CURSO; las sesiones
// (`registros`) históricas NO guardan qué empleados participaron, así que no
// se muestran sus fechas, participantes ni PDFs, ni se infiere asistencia.
// Sólo un registro que traiga `empleados` (parche de backend M.2B.4, aún no
// desplegado) y que incluya a este empleado se muestra como suyo. La
// comparación es EXACTA sobre String(id), igual que el backend (col BI y
// Empleados_Vinculados): ambos lados salen de la misma celda (String(row[0])).
// Nunca se usa fecha, conteo, posición ni nombre del PDF para atribuir.
export function registrosDelEmpleado(cap, idEmpleado) {
  const id = String(idEmpleado == null ? '' : idEmpleado).trim();
  if (!id) return [];
  return (cap.registros || [])
    .map((r, orden) => ({ r, orden }))
    .filter(({ r }) => Array.isArray(r.empleados) && r.empleados.some(e => String(e == null ? '' : e).trim() === id))
    .sort((a, b) => String(b.r.fecha || '').localeCompare(String(a.r.fecha || '')) || b.orden - a.orden)
    .map(({ r }) => r);
}

// "2026-09-30" -> "30 sep 2026" en fecha LOCAL (sin conversión UTC que cambie el día).
export function fechaRegistro(valor) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(valor || ''));
  if (!m) return String(valor || '').trim();
  const f = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (f.getMonth() !== Number(m[2]) - 1) return String(valor).trim();
  return f.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }).replace(/\./g, '');
}

export function CapacitacionesMobile({ recurso, onReintentar, ErrorCarga, idEmpleado, nombreEmpleado, guardiaSalida }) {
  let contenido;
  if (recurso.estado === 'error') {
    contenido = <ErrorCarga texto="No se pudieron cargar las capacitaciones." onReintentar={onReintentar} />;
  } else if (recurso.estado !== 'listo') {
    contenido = <Cargando etiqueta="Cargando capacitaciones" />;
  } else if ((recurso.datos || []).length === 0) {
    contenido = <Vacio icono="ti-school" texto="Sin capacitaciones asociadas." />;
  } else {
    const txt = v => String(v == null ? '' : v).trim();
    contenido = (
      <>
        <p className="m-sec-intro">Cursos asociados al expediente del empleado.</p>
        <ul className="m-sec-lista">
          {recurso.datos.map(cap => {
            const meta = [txt(cap.tema), txt(cap.duracion)].filter(Boolean).join(' · ');
            const detalle = [txt(cap.expositor), txt(cap.lugar)].filter(Boolean).join(' · ');
            const propios = registrosDelEmpleado(cap, idEmpleado);
            return (
              <li key={cap.id} className="m-sec-item">
                <div className="m-sec-item-cabecera">
                  <span className="m-sec-item-titulo">{txt(cap.nombre) || 'Capacitación'}</span>
                  {txt(cap.estatus) && <span className="m-sec-chip m-sec-chip-neutro">{txt(cap.estatus)}</span>}
                </div>
                {meta && <p className="m-sec-meta">{meta}</p>}
                {detalle && <p className="m-sec-meta">{detalle}</p>}
                {propios.length > 0 && (
                  <div className="m-reg-propios">
                    <p className="m-reg-titulo">{propios.length === 1 ? 'Registro' : 'Registros'}</p>
                    <ul className="m-reg-lista" aria-label="Registros de este empleado">
                      {propios.map((r, i) => (
                        <li key={i} className="m-reg-fila">
                          <span className="m-reg-fecha">{fechaRegistro(r.fecha) || 'Sin fecha'}</span>
                          {esHttps(r.url) && (
                            <a className="m-reg-ver" href={r.url} target="_blank" rel="noopener noreferrer">
                              Ver registro <i className="ti ti-external-link" aria-hidden="true"></i>
                            </a>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </>
    );
  }
  return (
    <>
      <RegistroCapacitacion
        idEmpleado={idEmpleado}
        nombreEmpleado={nombreEmpleado}
        guardiaSalida={guardiaSalida}
        onGuardado={onReintentar}
      />
      {contenido}
    </>
  );
}

// ─── Documentos ─────────────────────────────────────────────────────────────
// Fuente: api/empleado/anexos (carpeta Documentos_Firmados en Drive) +
// url_registro_capacitacion y carpeta_drive del detalle (mismas reglas que
// Desktop: el registro sólo se enlaza si es https). Mobile puede AGREGAR
// documentos (CargaDocumento); no genera paquete ni comprobante y no elimina.
function iconoArchivo(tipo, nombre) {
  const t = `${tipo || ''} ${nombre || ''}`.toLowerCase();
  if (t.includes('pdf')) return 'ti-file-type-pdf';
  if (/image|\.jpe?g|\.png/.test(t)) return 'ti-photo';
  return 'ti-file';
}

function fechaDocumento(valor) {
  const f = new Date(valor);
  return Number.isNaN(f.getTime()) ? '' : f.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
}

function EnlaceDocumento({ href, icono, titulo, meta }) {
  return (
    <a className="m-doc" href={href} target="_blank" rel="noopener noreferrer">
      <span className="m-doc-icono" aria-hidden="true"><i className={`ti ${icono}`}></i></span>
      <span className="m-doc-texto">
        <span className="m-doc-titulo">{titulo}</span>
        {meta && <span className="m-doc-meta">{meta}</span>}
      </span>
      <i className="ti ti-external-link m-doc-abrir" aria-hidden="true"></i>
    </a>
  );
}

export function DocumentosMobile({ anexos, onReintentar, detalle, ErrorCarga, idEmpleado, nombreEmpleado, guardiaSalida }) {
  const registro = detalle && esHttps(detalle.url_registro_capacitacion) ? detalle.url_registro_capacitacion : null;
  const carpeta = detalle && String(detalle.carpeta_drive || '').trim();
  const otros = [
    registro && { href: registro, icono: 'ti-scan', titulo: 'Registro de capacitación de ingreso', meta: 'Documento escaneado' },
    carpeta && { href: `https://drive.google.com/drive/folders/${encodeURIComponent(carpeta)}`, icono: 'ti-brand-google-drive', titulo: 'Carpeta del expediente en Drive', meta: 'Abre Google Drive' }
  ].filter(Boolean);

  let firmados;
  if (anexos.estado === 'error') {
    firmados = <ErrorCarga texto="No se pudieron cargar los documentos firmados." onReintentar={onReintentar} />;
  } else if (anexos.estado !== 'listo') {
    firmados = <Cargando etiqueta="Cargando documentos" />;
  } else if ((anexos.datos || []).length === 0) {
    firmados = <p className="m-sec-nota"><i className="ti ti-file-off" aria-hidden="true"></i> Sin documentos firmados anexados.</p>;
  } else {
    firmados = (
      <ul className="m-doc-lista">
        {anexos.datos.map(a => (
          <li key={a.id}>
            {esHttps(a.url)
              ? <EnlaceDocumento href={a.url} icono={iconoArchivo(a.tipo, a.nombre)} titulo={a.nombre || 'Documento'} meta={fechaDocumento(a.fecha)} />
              : <div className="m-doc m-doc-sin-enlace"><span className="m-doc-icono" aria-hidden="true"><i className={`ti ${iconoArchivo(a.tipo, a.nombre)}`}></i></span><span className="m-doc-texto"><span className="m-doc-titulo">{a.nombre || 'Documento'}</span></span></div>}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <>
      <CargaDocumento
        idEmpleado={idEmpleado}
        nombreEmpleado={nombreEmpleado}
        anexos={anexos}
        onRefrescar={onReintentar}
        guardiaSalida={guardiaSalida}
      />
      <section className="m-sec-grupo" aria-labelledby="m-doc-firmados">
        <h2 id="m-doc-firmados" className="m-exp-titulo">Documentos firmados</h2>
        {firmados}
      </section>
      {otros.length > 0 && (
        <section className="m-sec-grupo" aria-labelledby="m-doc-otros">
          <h2 id="m-doc-otros" className="m-exp-titulo">Otros documentos</h2>
          <ul className="m-doc-lista">
            {otros.map(o => <li key={o.titulo}><EnlaceDocumento {...o} /></li>)}
          </ul>
        </section>
      )}
    </>
  );
}
